import React, { useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { I } from "services/lang/LanguageService";
import DatasetButton from "./DatasetButton";
import {
  IUniversalDatasetTableColumn,
  IUniversalDatasetTableRow,
} from "./universal-dataset-table-editor/CellValuesEditor";
import { ColumnOptions } from "./universal-dataset-table-editor/EditColumn";
import { CellType } from "./universal-dataset-table-editor/EditDatasetCellDialog";

interface UniversalDatasetTableRowProps {
  rowData: IUniversalDatasetTableRow;
  columnsOptions: ColumnOptions[];
  updateFrequency?: number;
  compactMode?: boolean;
  referenceTime?: string;
}

const UniversalDatasetTableRow = ({
  rowData,
  columnsOptions,
  updateFrequency,
  compactMode = false,
  referenceTime,
}: UniversalDatasetTableRowProps) => {
  const datasetValues = useMemo(
    () =>
      rowData.columns
        .filter(
          (col: IUniversalDatasetTableColumn) =>
            col.cell.type === CellType.DATASET && col.cell.value != null
        )
        .map((col: IUniversalDatasetTableColumn) => col.cell.value),
    [rowData]
  );

  const { data: recentData } = useGetRecentData(datasetValues as string[], {
    count: 1,
    refTime: referenceTime && new Date(referenceTime).toISOString(),
    options: {
      refetchInterval: updateFrequency ? updateFrequency * 1000 : 0,
    },
  });

  const renderCellContent = (col: IUniversalDatasetTableColumn, index: number) => {
    if (
      col.cell.type === CellType.STRING ||
      col.cell.type === CellType.NUMBER ||
      col.cell.type === CellType.UNIT
    ) {
      return (
        <div>
          {col.cell.type === CellType.STRING && typeof col.cell.value === "string"
            ? I(col.cell.value)
            : col.cell.value}
        </div>
      );
    }

    if (col.cell.type === CellType.DATASET) {
      const dataset = recentData?.find(ds => ds.identifier === col.cell.value);
      if (dataset) {
        return (
          <DatasetButton
            dataset={dataset}
            columnIndex={index}
            rowData={rowData}
            compactMode={compactMode}
          />
        );
      }
    }
    return null;
  };

  return (
    <tr>
      {rowData.isSeparator ? (
        <td colSpan={columnsOptions.length + 1}>
          <div style={{ height: "15px" }} />
        </td>
      ) : (
        rowData.columns.map((col: IUniversalDatasetTableColumn, index: number) => {
          if (!columnsOptions[index]?.columnVisibility) return null;

          return (
            <td
              key={uuidv4()}
              style={{
                color: col.cell.highlightColor?.color,
                backgroundColor: col.cell.highlightColor?.bgColor,
              }}
            >
              {renderCellContent(col, index)}
            </td>
          );
        })
      )}
    </tr>
  );
};

export default UniversalDatasetTableRow;
